import { useCallback, useReducer, useRef } from 'react';
import type { ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import {
  INITIAL_VERSIONS,
  versionFilesUrls,
  versionsReducer,
} from './versionsReducer';
import { INITIAL_RESTORE_EVENTS, restoreEventsReducer } from './restoreEvents';
import { requestVersionFiles } from './versionFiles';
import { useSyncReducer } from './useSyncReducer';

export interface UseVersionsOptions {
  getItemCount: () => number;
  onUnknownRestore: () => void;
  filesWarning: string;
}

export const useVersions = (options: UseVersionsOptions) => {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [versions, dispatch, versionsRef] = useSyncReducer(
    versionsReducer,
    INITIAL_VERSIONS
  );
  const [{ events: restoreEvents }, dispatchRestoreEvent] = useReducer(
    restoreEventsReducer,
    INITIAL_RESTORE_EVENTS
  );
  const requestedFilesRef = useRef(new Set<string>());

  const handleServerMessage = useCallback(
    (message: ServerMessage, meta: PiChatFrameMeta) => {
      const before = dispatch({ type: 'server', message });
      requestVersionFiles(
        versionFilesUrls(message),
        meta,
        requestedFilesRef.current,
        {
          onLoaded: (snapshotId, files) =>
            dispatch({ type: 'filesLoaded', snapshotId, files }),
          onError: (error) =>
            console.warn(optionsRef.current.filesWarning, error),
        }
      );
      if (message.type === 'snapshot.restored') {
        dispatchRestoreEvent({
          type: 'restored',
          snapshotIds: before.snapshots.map(({ id }) => id),
          previousPointer: before.pointer,
          restoredSnapshotId: message.snapshotId,
          afterCount: optionsRef.current.getItemCount(),
        });
        if (!before.snapshots.some(({ id }) => id === message.snapshotId)) {
          optionsRef.current.onUnknownRestore();
        }
      }
    },
    [dispatch]
  );

  const reset = useCallback(() => {
    requestedFilesRef.current = new Set();
    dispatchRestoreEvent({ type: 'connecting' });
    dispatch({ type: 'connecting' });
  }, [dispatch]);

  const restore = useCallback(
    async (
      snapshotId: string,
      sendRestore: (snapshotId: string) => Promise<boolean>
    ) => {
      if (versionsRef.current.restoringSnapshotId !== null) return;
      dispatch({ type: 'restoreRequested', snapshotId });
      if (!(await sendRestore(snapshotId))) {
        dispatch({ type: 'restoreAborted' });
      }
    },
    [dispatch, versionsRef]
  );

  return {
    versions,
    versionsRef,
    restoreEvents,
    handleServerMessage,
    reset,
    restore,
  };
};
